import { CommonModule } from '@angular/common';
import { Component, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CachedPortfolioService } from '@cfp/core';
import { BuildingAddress } from '@cfp/shared/building/address-module';
import { Combobox, OrderByModule } from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { BuildingAddressLinesDTO } from 'csr-asset-app';

export interface AddressSelectedEvent {
  address: string[];
  name?: string;
}

@Component({
  selector: 'cfp-select-building-combobox',
  templateUrl: './select-building-combobox.html',
  imports: [BuildingAddress, RouterModule, CommonModule, OrderByModule, FormsModule, Combobox, TranslateModule]
})
export class SelectBuildingCombobox {
  public readonly portfolioId = input<number>();
  public readonly buildingId = input<number>();
  public readonly showBuildingName = input<boolean>(false);
  public readonly combineNameAndAddress = input<boolean>(false);

  public readonly selected = output<AddressSelectedEvent>();

  public searchValue: string;

  protected noAddressesFound: boolean = false;
  protected addresses?: BuildingAddressLinesDTO[];
  protected activeAddress?: BuildingAddressLinesDTO;

  private modulePath: string;

  private readonly cachedPortfoliosService = inject(CachedPortfolioService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  constructor() {
    if (this.activatedRoute.snapshot.data?.modulePath) {
      this.modulePath = this.activatedRoute.snapshot.data?.modulePath;
    } else if (this.activatedRoute.parent?.parent?.snapshot.url?.length > 0) {
      this.modulePath = this.activatedRoute.parent.parent.snapshot.url[0].path;
    } else if (this.activatedRoute.parent?.snapshot.url?.length > 0) {
      this.modulePath =
        this.activatedRoute.parent.snapshot.url[0].path ||
        (this.activatedRoute.snapshot.data && this.activatedRoute.snapshot.data.modulePath);
    } else if (this.activatedRoute.snapshot.url?.length > 0) {
      const urlPath = this.activatedRoute.snapshot.url;
      this.modulePath = this.activatedRoute.snapshot.data?.modulePath || urlPath[0].path;
    }
  }

  protected loadAddresses(open: boolean): void {
    if (!open) {
      return;
    }

    this.cachedPortfoliosService.v2PortfoliosIdBuildingAddressLinesGet(this.portfolioId()).subscribe({
      next: (addresses: BuildingAddressLinesDTO[]) => {
        this.addresses = addresses;
        this.activeAddress = this.addresses.find((address) => address.buildingId === this.buildingId());
      }
    });
  }

  protected addressSelected(addressLine?: BuildingAddressLinesDTO): void {
    if (!addressLine) {
      return;
    }

    this.searchValue = undefined;
    this.selected.emit({ address: addressLine.addressLines, name: addressLine.name });

    const buildingMeasureId = this.activatedRoute.snapshot.params.buildingMeasureId;
    if (this.buildingId()) {
      if (buildingMeasureId) {
        this.router.navigateByUrl(`/${this.modulePath}/${this.portfolioId()}/buildings/${addressLine.buildingId}`);
      } else {
        this.router.navigateByUrl(
          `/${this.modulePath}/${this.portfolioId()}/buildings/${addressLine.buildingId}${this.extraPath(4 + this.modulePath.split('/').length)}`
        );
      }
    }
  }

  private extraPath(position: number): string {
    // Stay on same page when switching building
    return this.router.url
      .split('/')
      .splice(position)
      .reduce((acc, val) => `${acc}/${val}`, '');
  }
}
